// ============================================
// Exploit Chain – domain logic (no DOM)
// ============================================

export type DefenseState = {
  canary: boolean;
  aslr: boolean;
  dep: boolean;
  bounds: boolean;
};

export type DefenseEffect = {
  blocksAt: number;
  title: string;
  desc: string;
  bypassNote?: string;
};

export type ChainStage = {
  title: string;
  desc: string;
  payload: string;
  stack: {
    locals: string;
    canary: string;
    rbp: string;
    rip: string;
  };
  corrupted: string[];
  highlight: string[];
};

export const DEFENSE_EFFECTS: Record<keyof DefenseState, DefenseEffect> = {
  bounds: {
    blocksAt: 1,
    title: 'Exploit Prevented: Bounds Checking',
    desc: 'Safe string functions (strncpy, snprintf) prevent the overflow from occurring. The copy is truncated to buffer size.',
  },
  canary: {
    blocksAt: 3,
    title: 'Exploit Detected: Stack Canary',
    desc: 'The canary value was corrupted during overflow. The program calls __stack_chk_fail() and terminates before returning.',
    bypassNote: 'Can be bypassed with info leak to read canary value.',
  },
  aslr: {
    blocksAt: 4,
    title: 'Exploit Complicated: ASLR',
    desc: 'Address Space Layout Randomization means the attacker cannot predict where to jump. Return address points to invalid memory.',
    bypassNote: 'Can be bypassed with info leak or brute force (32-bit).',
  },
  dep: {
    blocksAt: 4,
    title: 'Exploit Blocked: DEP/NX',
    desc: 'Data Execution Prevention marks the stack as non-executable. CPU raises exception when trying to execute shellcode.',
    bypassNote: 'Can be bypassed with Return-Oriented Programming (ROP).',
  },
};

export const CHAIN_STAGES: ChainStage[] = [
  {
    title: 'Stage 1: Vulnerability Identified',
    desc: 'The vulnerable function uses strcpy() without bounds checking. An attacker-controlled input of arbitrary length will be copied into a fixed-size stack buffer.',
    payload: `<span class="code-comment">// Vulnerable function identified</span>
<span class="code-type">void</span> <span class="code-fn">process_input</span>(<span class="code-type">char</span> *user_input) {
    <span class="code-type">char</span> buffer[<span class="code-num">16</span>];
    <span class="code-fn">strcpy</span>(buffer, user_input);  <span class="code-comment">// No bounds check!</span>
}`,
    stack: {
      locals: '[ buffer[16] ]',
      canary: '0xDEADBEEF',
      rbp: '0x7fff1234',
      rip: '0x401156',
    },
    corrupted: [],
    highlight: ['locals'],
  },
  {
    title: 'Stage 2: Buffer Overflow Triggered',
    desc: 'Attacker sends 32 bytes of input. The first 16 fill the buffer, then 8 bytes overwrite the stack canary, and the next 8 overwrite saved RBP.',
    payload: `<span class="code-comment">// Payload: 32 bytes total</span>
payload = <span class="code-str">"A"</span> * <span class="code-num">16</span>   <span class="code-comment">// Fill buffer</span>
        + <span class="code-str">"B"</span> * <span class="code-num">8</span>    <span class="code-comment">// Overwrite canary</span>
        + <span class="code-str">"C"</span> * <span class="code-num">8</span>    <span class="code-comment">// Overwrite saved RBP</span>

<span class="code-comment">// Memory after overflow:</span>
<span class="code-comment">// buffer:  AAAAAAAAAAAAAAAA</span>
<span class="code-comment">// canary:  BBBBBBBB (corrupted!)</span>
<span class="code-comment">// rbp:     CCCCCCCC (corrupted!)</span>`,
    stack: {
      locals: 'AAAAAAAAAAAAAAAA',
      canary: '0x42424242',
      rbp: '0x43434343',
      rip: '0x401156',
    },
    corrupted: ['canary', 'rbp'],
    highlight: ['canary', 'rbp'],
  },
  {
    title: 'Stage 3: Return Address Overwritten',
    desc: 'Extending the payload to 40 bytes overwrites the saved return address (RIP). The attacker controls where execution will jump when the function returns.',
    payload: `<span class="code-comment">// Payload: 40 bytes - includes RIP overwrite</span>
payload = <span class="code-str">"A"</span> * <span class="code-num">16</span>           <span class="code-comment">// Fill buffer</span>
        + <span class="code-str">"B"</span> * <span class="code-num">8</span>            <span class="code-comment">// Overwrite canary</span>
        + <span class="code-str">"C"</span> * <span class="code-num">8</span>            <span class="code-comment">// Overwrite saved RBP</span>
        + <span class="code-fn">p64</span>(<span class="code-num">0xdeadbeef</span>)  <span class="code-comment">// Overwrite RIP!</span>

<span class="code-comment">// Attacker now controls return address</span>
<span class="code-comment">// RIP = 0xdeadbeef (attacker-chosen)</span>`,
    stack: {
      locals: 'AAAAAAAAAAAAAAAA',
      canary: '0x42424242',
      rbp: '0x43434343',
      rip: '<span class="code-danger">0xdeadbeef</span>',
    },
    corrupted: ['canary', 'rbp', 'rip'],
    highlight: ['rip'],
  },
  {
    title: 'Stage 4: Control Flow Hijacked',
    desc: 'When the function executes "ret", the CPU pops the corrupted RIP value and jumps to the attacker\'s address. The attacker has hijacked program execution.',
    payload: `<span class="code-comment">// Function epilogue executes:</span>
<span class="code-keyword">leave</span>    <span class="code-comment">// mov rsp, rbp; pop rbp</span>
<span class="code-keyword">ret</span>      <span class="code-comment">// pop rip; jmp rip</span>

<span class="code-comment">// CPU state after ret:</span>
<span class="code-comment">// RIP = 0xdeadbeef  ← Attacker controls this!</span>
<span class="code-comment">// Execution jumps to attacker's code</span>

<span class="code-comment">// Common targets:</span>
<span class="code-comment">// - Shellcode in buffer</span>
<span class="code-comment">// - ROP gadget chain</span>
<span class="code-comment">// - Return-to-libc (system("/bin/sh"))</span>`,
    stack: {
      locals: '[ shellcode... ]',
      canary: '--------',
      rbp: '--------',
      rip: '<span class="code-danger">→ JMP!</span>',
    },
    corrupted: ['canary', 'rbp', 'rip'],
    highlight: ['rip'],
  },
  {
    title: 'Stage 5: Arbitrary Code Execution',
    desc: 'The attacker achieves code execution. In this example, they spawn a shell. Modern exploits use ROP chains to bypass DEP/NX, and info leaks to bypass ASLR.',
    payload: `<span class="code-comment">// Exploit payload spawns a shell</span>
<span class="code-comment">// Using return-to-libc technique:</span>

payload = <span class="code-str">"A"</span> * <span class="code-num">16</span>
        + <span class="code-str">"B"</span> * <span class="code-num">8</span>
        + <span class="code-str">"C"</span> * <span class="code-num">8</span>
        + <span class="code-fn">p64</span>(pop_rdi)       <span class="code-comment">// ROP gadget</span>
        + <span class="code-fn">p64</span>(bin_sh_addr)   <span class="code-comment">// "/bin/sh" string</span>
        + <span class="code-fn">p64</span>(system_addr)   <span class="code-comment">// system()</span>

<span class="code-comment">// Result: system("/bin/sh")</span>
<span class="code-comment">// Attacker gets shell access!</span>`,
    stack: {
      locals: '[ pwned ]',
      canary: '--------',
      rbp: '--------',
      rip: '<span class="code-danger">system()</span>',
    },
    corrupted: ['canary', 'rbp', 'rip', 'locals'],
    highlight: [],
  },
];

// --- Helpers ---

export function getActiveDefenses(defenses: DefenseState): (keyof DefenseState)[] {
  return (Object.keys(defenses) as (keyof DefenseState)[]).filter((k) => defenses[k]);
}

export function getEarliestBlock(defenses: DefenseState): DefenseEffect | null {
  const active = getActiveDefenses(defenses);
  if (active.length === 0) return null;

  let earliest: DefenseEffect | null = null;
  let earliestStage = Infinity;

  for (const defense of active) {
    const effect = DEFENSE_EFFECTS[defense];
    if (effect.blocksAt < earliestStage) {
      earliestStage = effect.blocksAt;
      earliest = effect;
    }
  }

  return earliest;
}
